import styled from "@emotion/styled";
import { NavbarButtonOptions, getNavbarButtonOptions } from "./NavbarV2Desktop";
import { WavyClose, WavyHamburger, WavyHamburgerExpanded } from "components/Icons";
import { Link, useLocation } from "react-router-dom";
import { useMemo, useRef } from "react";
import { useIsElementActive } from "hooks/useIsElementActive";
import { GlobalStore } from "store/global/GlobalStore";

interface NavbarV2MobileProps {
  isMoreOpen: boolean;
  setIsMoreOpen: React.Dispatch<React.SetStateAction<boolean>>;
}
export const NavbarV2Mobile = ({ isMoreOpen, setIsMoreOpen }: NavbarV2MobileProps) => {
  const theme = GlobalStore.useState(s => s.theme);
  const iconSize = { width: 28, height: 28 };
  const moreButtonRef = useRef<HTMLButtonElement | null>(null);
  const isMoreButtonActive = useIsElementActive(moreButtonRef);

  const MoreIconRaw = useMemo(() => {
    if (isMoreOpen) return WavyClose;
    if (isMoreButtonActive) return WavyHamburgerExpanded;
    return WavyHamburger;
  }, [isMoreButtonActive, isMoreOpen]);

  const MoreIcon = styled(MoreIconRaw)`
    pointer-events: none;
  `;

  return (
    <MobileNavbarContainer>
      {/* Page nav items */}
      {getNavbarButtonOptions(theme).map(option => (
        <Button
          key={option.text}
          iconSize={iconSize}
          option={option}
          closeMoreMenu={() => setIsMoreOpen(false)}
        />
      ))}

      {/* More nav item */}
      <MobileNavbarButton ref={moreButtonRef} onTouchEnd={() => setIsMoreOpen(!isMoreOpen)}>
        <MoreIcon {...iconSize} />
      </MobileNavbarButton>
    </MobileNavbarContainer>
  );
};

// Sub-components

const MobileNavbarContainer = styled.div`
  position: sticky;
  bottom: 0;
  display: flex;
  justify-content: space-around;
  width: 100%;
  height: 68px;
  padding: 6px;
  flex-direction: row;
  align-items: flex-start;
  gap: 10px;
  z-index: 9200;
  background-color: ${props => props.theme.surface.alt.primary.default};
`;

const MobileNavbarButton = styled.button`
  padding: 12px;
  height: 56px;
  width: 56px;
  display: flex;
  justify-content: center;
  align-items: center;
  transition: width 0.2s ease, height 0.2s ease;
  border: none;
  background: none;

  &:active {
    svg {
      width: 26px;
      height: 26px;
    }
  }
`;

interface ButtonProps {
  iconSize: { width: number; height: number };
  option: NavbarButtonOptions;
  closeMoreMenu: () => void;
}

const Button = ({ iconSize, option, closeMoreMenu }: ButtonProps) => {
  const currentPathName = useLocation().pathname;
  const isSelected = currentPathName === option.pathName;
  const ref = useRef<HTMLButtonElement>(null);
  const Icon = isSelected ? option.symbol.active : option.symbol.default;

  return (
    <Link to={option.pathName ?? ""} onClick={closeMoreMenu}>
      <MobileNavbarButton ref={ref}>
        <Icon {...iconSize} />
      </MobileNavbarButton>
    </Link>
  );
};
